import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RestOptions } from '../../../../services/rest/rest-request';
import { RestService } from '../../../../services/rest/rest.service';
import { Currency } from '../../../../domain/market.domain';

import {
  CancelOrderParameters,
  CancelOrderRequest,
  Order,
  OrderCapture,
  OrderRequest,
  OrderTerms
} from '../../domain/order.domain';
import { OrderDetailsParameters } from '../domain/order-entry.domain';

@Injectable()
export class OrderEntryRestService {
  private readonly restService = inject(RestService);

  validateOrder(order: OrderRequest): Observable<OrderCapture> {
    return this.restService.getResource('order_v3.createCapture')
      .post<OrderRequest, OrderCapture>(order)
      .pipe(
        map(this.mapCurrencySymbol));
  }

  validateUpdateOrder(order: OrderRequest, params: OrderDetailsParameters): Observable<OrderCapture> {
    const parameters: RestOptions = {
      pathVariables: { orderKey: params.orderKey }
    };
    return this.restService.getResource('order_v3.updateCapture')
      .put<OrderRequest, OrderCapture>(order, parameters)
      .pipe(
        map(this.mapCurrencySymbol)
      );
  }

  commitUpdateOrder(order: OrderRequest, params: OrderDetailsParameters): Observable<OrderCapture> {
    const parameters: RestOptions = {
      pathVariables: { orderKey: params.orderKey }
    };
    return this.restService.getResource('order_v3.update').put(order, parameters);
  }

  createTradingOrder(order: OrderRequest<OrderTerms>): Observable<OrderCapture> {
    return this.restService.getResource(('order_v3.create')).post(order);
  }

  fetchOrderDetails(params: OrderDetailsParameters, reverse: boolean): Observable<OrderCapture> {
    const parameters: RestOptions = {
      pathVariables: { orderKey: params.orderKey },
      params: {
        requestFills: params.requestFills
      }
    };
    const restEndpoint = reverse ? 'order_v3.reverse' : 'order_v3.captureDetail';
    return this.restService.getResource(restEndpoint).get<OrderCapture>(parameters);
  }

  fetchHistoricalOrderDetails(params: OrderDetailsParameters, reverse: boolean): Observable<OrderCapture> {
    const parameters: RestOptions = {
      pathVariables: { orderKey: params.orderKey },
      params: {
        requestFills: params.requestFills
      }
    };
    const restEndpoint = reverse ? 'order_v3.historicalReverse' : 'order_v3.historicalDetail';
    return this.restService.getResource(restEndpoint).get<OrderCapture>(parameters);

  }

  validateOrderDetails(order: Order): Observable<OrderCapture> {
    return this.restService.getResource('order_v3.captureDetail').put<OrderRequest, OrderCapture>(order);
  }

  sendCancelOrderRequest(params: CancelOrderParameters): Observable<OrderCapture> {
    const parameters: RestOptions = {
      pathVariables: { orderKey: params.orderKey }
    };
    return this.restService.getResource('order_v3.delete').put<CancelOrderRequest, OrderCapture>(params.cancelOrderRequest, parameters);
  }

  private mapCurrencySymbol(result: OrderCapture): OrderCapture {
    if (result?.orderTerms?.order?.[0]?.orderEstimate?.currencySymbol === 'CAN' as Currency) {
      result.orderTerms.order[0].orderEstimate.currencySymbol = Currency.CAD;
    }
    return result;
  }
}

